import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect
} from 'react';
import {
    WebSocketMessage,
    ResultMessage,
    TimeoutMessage,
    ErrorMessage,
    CheckMessage
} from '../../services/websocket/types';
import { couponWsService } from '../../services/websocket/couponWebSocket';
import { couponStorageService } from '../../services/storage/couponStorage';
import { useSelector, useDispatch } from 'react-redux';
import { StoreType } from '../../store/spirs/types';
import { saveCoupon, SET_COUPON_STATE, UPDATE_COUPON_MESSAGE } from '../../store/spirs/actions';
import {useTranslation} from "react-i18next";
import {CouponForm} from "../../models/api/codegen/code";
import { CouponState } from '../../store/spirs/reducers/couponReducer';

interface CouponOverlayContextType {
    showOverlay: boolean;
    countdown: number;
    initialTimeout: number;
    startTimer: () => void;
    saveCouponWithWebSocket: (couponForm: CouponForm, originalCoupons: CouponState) => Promise<void>;
    isConnecting: boolean;
    connectionError: string | null;
    couponMessage: string | null;
}

const CouponOverlayContext = createContext<
    CouponOverlayContextType | undefined
>(undefined);

export const useTestOverlay = () => {
    const context = useContext(CouponOverlayContext);
    if (!context) {
        throw new Error('ошибка использования CouponOverlay');
    }
    return context;
};

export const TestOverlayProvider: React.FC<{ children: ReactNode }> = ({
    children
}) => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {currentCouponType} = useSelector((state: StoreType) => ({
        currentCouponType: state.spirs.coupons.currentCouponType,
    }));

    // Получаем sessionToken из Redux store
    const sessionToken = useSelector(
        (state: StoreType) => state.spirs.playerToken
    );

    const [showOverlay, setShowOverlay] = useState(false);
    const [countdown, setCountdown] = useState(0); // Будет установлено от сервера
    const [initialTimeout, setInitialTimeout] = useState(0); // Будет установлено от сервера
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [couponMessage, setCouponMessage] = useState<string | null>(null);
    const [currentTimer, setCurrentTimer] = useState<number | null>(null);
    const [backendResponseReceived, setBackendResponseReceived] = useState(
        false
    );
    const backendResponseRef = React.useRef(false);


    // Проверка ожидающих купонов при монтировании
    useEffect(() => {
        couponWsService.disconnect();
        if (sessionToken !== 'GUEST') {
            checkPendingCoupon();
        } else {
            stopTimer();
        }

        return () => {
            if (currentTimer) {
                clearInterval(currentTimer);
            }
            couponWsService.disconnect();
        };
    }, [sessionToken]);

    const checkPendingCoupon = () => {
        if (couponStorageService.hasPendingCoupon()) {
            const remainingTime = couponStorageService.getRemainingTime();
            if (remainingTime > 0) {
                startCountdownWithTime(remainingTime);
            }
            // Проверка статуса купона
            checkCouponStatus();
        }
    };

    const checkCouponStatus = async () => {
        console.log(new Date(), '[COUPON WS] check coupon state')
        const data = couponStorageService.get();
        if (!data) {
            return;
        }

        try {
            couponWsService.setWsQueueId(data.wsQueueId);

            await couponWsService.connect(handleWebSocketMessage);

            couponWsService.sendCheckCoupon({
                couponBetId: data.couponBetId,
                sessionToken: sessionToken,
                wsQueueId: data.wsQueueId
            });

        } catch (error) {
            setConnectionError(
                'Ошибка подключения при проверке статуса купона'
            );
        }
    };

    const startTimer = () => {
        startCountdownWithTime(20);
    };

    const startCountdownWithTime = (timeInSeconds: number) => {
        setShowOverlay(true);
        setCountdown(timeInSeconds);
        setInitialTimeout(timeInSeconds);
        setCouponMessage('Обработка купона...');
        setBackendResponseReceived(false); 

        if (currentTimer) {
            clearInterval(currentTimer);
        }

        const timer = (setInterval(() => {
            setCountdown(prev => {
                // Если таймер закончился и ответ получен, скрываем оверлей
                console.log('[COUPON WS] startCountdownWithTime ', prev, backendResponseRef.current)
                if (prev <= 1 && backendResponseRef.current) {
                    clearInterval(timer);
                    setShowOverlay(false);
                    setIsConnecting(false); // Добавляем сброс isConnecting
                    setCouponMessage(null);
                    setCurrentTimer(null);

                    // Принудительное обновление через setTimeout
                    setTimeout(() => {
                        setShowOverlay(false);
                        setIsConnecting(false);
                    }, 0);

                    return 0;
                } else if (prev <= 1) {
                    // Если таймер закончился, но ответ не пришел - продолжаем показывать оверлей
                    clearInterval(timer);
                    setCouponMessage('Ожидание ответа от сервера...');
                    setCurrentTimer(null);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000) as unknown) as number;

        setCurrentTimer(timer);
    };

    const stopTimer = () => {
        if (currentTimer) {
            clearInterval(currentTimer);
            setCurrentTimer(null);
        }
        setShowOverlay(false);
        setIsConnecting(false); 
        setCountdown(0);
        setCouponMessage(null);

        // Принудительное обновление через setTimeout
        setTimeout(() => {
            setShowOverlay(false);
            setIsConnecting(false);
        }, 0);

    };

    const handleWebSocketMessage = (message: WebSocketMessage) => {
        console.log('[COUPON WS] handleWebSocketMessage', message)
        switch (message.type) {
            case 'RESULT':
                handleResultMessage(message as ResultMessage);
                break;
            case 'TIMEOUT':
                handleTimeoutMessage(message as TimeoutMessage);
                break;
            case 'ERROR':
                handleErrorMessage(message as ErrorMessage);
                break;
            case 'CHECK':
                handleCheckMessage(message as CheckMessage);
                break;
            default:
                console.warn(
                    '[TestOverlayContext] ❓ Неизвестный тип сообщения:',
                    message.type
                );
        }
    };

    // Обработка успешного сохранения купона - обновление данных игрока и т.д.
    const handleResultMessage = (message: ResultMessage) => {
        couponStorageService.remove();
        setBackendResponseReceived(true);
        setCouponMessage('Купон сохранен, проверяем статус...');

        // Обновляем данные игрока в Redux store
        dispatch(saveCoupon.success(message.payload));

        couponWsService.disconnect();

        // Проверяем статус купона (checkCouponStatus создаст новое подключение)
        checkCouponStatus();

    };

    const handleTimeoutMessage = (message: TimeoutMessage) => {

        const timeoutInSeconds = message.payload.stepCheckingTimeout;
        const couponBetId = message.payload.couponBetId;

        // Сохранение данных в хранилище с wsQueueId
        const wsQueueId = couponWsService.getWsQueueId();


        if (wsQueueId) {
            const storageData = {
                couponBetId,
                timeout: timeoutInSeconds,
                receivedAt: Date.now(),
                wsQueueId: wsQueueId
            };
            couponStorageService.save(storageData);
        } else {
            console.error(
                '[TestOverlayContext] ❌ Нет wsQueueId для сохранения в storage'
            );
        }

        // Запуск обратного отсчета
        startCountdownWithTime(timeoutInSeconds);
    };

    const handleErrorMessage = (message: ErrorMessage) => {
        console.error(
            '[TestOverlayContext] Ошибка купона:',
            message.payload
        );
        setBackendResponseReceived(true);
        // Сообщение о высокой нагрузке системы
        if (message.payload === 'save coupon rejected due to overload') {
            dispatch(UPDATE_COUPON_MESSAGE(t("coupons.highLoadedMessage")));
        } else if (message.payload === 'swip.error.1012') {
            dispatch(UPDATE_COUPON_MESSAGE(t("coupons." + message.payload,{eventType: currentCouponType})));
        } else {
            dispatch(UPDATE_COUPON_MESSAGE(t(message.payload)));
        }
        setCouponMessage(`Ошибка: ${message.payload}`);
        

        couponStorageService.remove();
        stopTimer();

        // Закрываем WebSocket соединение после ошибки
        couponWsService.disconnect();

    };

    const handleCheckMessage = (message: CheckMessage) => {

        // Правильная структура: message.payload.type и message.payload.payload
        console.log('[COUPON WS] handleCheckMessage', message)
        const checkType = message.payload.type;
        const checkPayload = message.payload.payload;

        if (!checkType) {
            console.error(
                '[TestOverlayContext] Некорректная структура CHECK сообщения:',
                message
            );
            return;
        }
        switch (checkType) {
            case 'DONE':
                setBackendResponseReceived(true); 
                setCouponMessage('Купон успешно обработан!');

                couponStorageService.remove();
                couponWsService.disconnect();

                stopTimer();

                // Обработка успешной обработки купона
                break;

            case 'IN_PROCESS':
                setCouponMessage('Купон обрабатывается...');
                let coupon = couponStorageService.getCoupon();
                if (coupon) {
                    dispatch(SET_COUPON_STATE(coupon));
                }
                // Продолжаем ожидание
                break;

            case 'FAILED':
                setBackendResponseReceived(true); // Отмечаем, что ответ получен
                setCouponMessage(
                    `Ошибка обработки: ${
                        typeof checkPayload === 'string'
                            ? checkPayload
                            : JSON.stringify(checkPayload)
                    }`
                );
                let couponMessage;
                if (checkPayload === 'Your card is not found') { // нужно, что бы СВИП слал коды
                    couponMessage = "coupons.swip.not-found";
                } else {
                    couponMessage = checkPayload;
                }
                let couponFailed = couponStorageService.getCoupon();
                if (couponFailed) {
                    dispatch(SET_COUPON_STATE({...couponFailed, message: couponMessage}));
                } else {
                    dispatch(UPDATE_COUPON_MESSAGE(couponMessage));
                }

                couponStorageService.remove();
                couponWsService.disconnect();
                stopTimer();

                break;

            case 'NOT_FOUND':
                setBackendResponseReceived(true); // Отмечаем, что ответ получен
                setCouponMessage('Купон не найден');

                couponStorageService.remove();
                couponWsService.disconnect();
                stopTimer();

                break;

            default:
                console.warn(
                    '[TestOverlayContext] Неизвестный тип результата проверки:',
                    checkType
                );
        }
    };

    const saveCouponWithWebSocket = async (
        couponForm: CouponForm,
        originalCoupons: CouponState
    ): Promise<void> => {
        try {
            // Показываем overlay сразу при начале процесса
            setShowOverlay(true);
            setIsConnecting(true);
            setConnectionError(null);
            setCouponMessage('Подключение...');

            // Используем существующий wsQueueId из сервиса или подключаемся без параметра
            const currentWsQueueId = couponWsService.getWsQueueId();

            // Подключаемся к WebSocket с обработчиком сообщений
            await couponWsService.connect(handleWebSocketMessage);

            setIsConnecting(false);
            setCouponMessage('Отправка купона...');

            couponWsService.sendSaveCoupon(couponForm);
            couponStorageService.saveCoupon(originalCoupons);

            setCouponMessage('Купон отправлен, ожидание ответа...');
        } catch (error) {
            setConnectionError('Ошибка подключения к серверу');
            setCouponMessage(`Ошибка: ${error}`);
            setIsConnecting(false);
            stopTimer();
            couponStorageService.removeCoupon();
        }
    };

    return (
        <CouponOverlayContext.Provider
            value={{
                showOverlay,
                countdown,
                initialTimeout,
                startTimer,
                saveCouponWithWebSocket,
                isConnecting,
                connectionError,
                couponMessage
            }}
        >
            {children}
        </CouponOverlayContext.Provider>
    );
};
